import "lib/@hooks/useEvent"
import { createEvent } from "library/local-events/index.mjs"

export const MessageProgramsUpdated = createEvent("MessageProgramsUpdated")

/** @type {Map<string, unknown>} */
const programs = new Map()

let currentLanguage = "en"
let currentNamespacesKey = "core"
/** @type {"idle"|"loading"|"ready"|"error"} */
let status = "idle"
let loadGeneration = 0

/**
 * @returns {{ language: string, namespacesKey: string, status: string, generation: number }}
 */
export function getProgramCacheMeta() {
    return { language: currentLanguage, namespacesKey: currentNamespacesKey, status, generation: loadGeneration }
}

/**
 * @param {string} messageId
 * @returns {unknown}
 */
export function getProgram(messageId) {
    return programs.get(messageId)
}

/**
 * @param {Record<string, unknown>|null|undefined} map
 * @param {string} language
 * @param {string} [namespacesKey]
 */
export function setPrograms(map, language, namespacesKey = "core") {
    programs.clear()
    if (map && typeof map === "object") {
        for (const [k, v] of Object.entries(map)) {
            programs.set(k, v)
        }
    }
    currentLanguage = language || "en"
    currentNamespacesKey = namespacesKey
    status = "ready"
    loadGeneration++
    MessageProgramsUpdated.raise()
}

/**
 * @param {string} language
 */
export function setProgramCacheLoading(language) {
    currentLanguage = language || "en"
    status = "loading"
    loadGeneration++
    MessageProgramsUpdated.raise()
}

/**
 * @param {string} language
 * @param {Error} [err]
 */
export function setProgramCacheError(language, err) {
    currentLanguage = language || "en"
    status = "error"
    if (err) console.warn("Message programs load error:", err?.message)
    loadGeneration++
    MessageProgramsUpdated.raise()
}

/**
 * Clear programs (e.g. language switched to en).
 */
export function clearPrograms() {
    programs.clear()
    status = "ready"
    loadGeneration++
    MessageProgramsUpdated.raise()
}

/**
 * Ignore stale responses: increment generation without clearing programs.
 * @returns {number} token to pass back to setProgramsIfCurrent
 */
export function beginLoad(language) {
    currentLanguage = language || "en"
    status = "loading"
    loadGeneration++
    const token = loadGeneration
    MessageProgramsUpdated.raise()
    return token
}

/**
 * @param {number} token
 * @param {Record<string, unknown>|null|undefined} map
 * @param {string} language
 * @param {string} [namespacesKey]
 */
export function setProgramsIfCurrent(token, map, language, namespacesKey = "core") {
    // eslint-disable-next-line security/detect-possible-timing-attacks -- integer generation counter, not a secret
    if (token !== loadGeneration) return false
    setPrograms(map, language, namespacesKey)
    return true
}

/**
 * @returns {boolean}
 */
export function isMessageProgramsLoading() {
    return status === "loading"
}
